import { DateTime } from 'luxon';
import type { MutableRefObject } from 'react';

import { QuoteStatus } from '@core/types/api/QuoteStatus/QuoteStatus';

import { DISCOUNT_AMOUNT_DECIMALS } from '../constants/discounts';
import type { ProjectOrQuoteProductFormikType } from '../formik/projectOrQuoteProduct';
import {
  ApprovedQuoteFormikType,
  matchProjectProductsToLines,
  ProjectProductToQuoteProductFormik,
  QuoteConfigProductToQuoteProductFormik,
  type ProductsByPlantFormikType,
  type QuoteFormikType,
} from '../formik/quote';
import {
  setRecalculatedRMCostsNoBag,
  setRecalculatedWithDeliveryCostsNoBag,
} from '../hooks/useProductSection';
import { Currency } from '../types/api/Currency/Currency';
import Enums from '../types/api/Enums';
import type { FixedPricingByCompany } from '../types/api/FixedPricingByCompany/FixedPricingByCompany';
import type { Product } from '../types/api/Product/Product';
import type { Project } from '../types/api/Project/Project';
import type { ProjectProduct } from '../types/api/ProjectProduct/ProjectProduct';
import type { QuoteConfig } from '../types/api/QuoteConfig/QuoteConfig';
import type { QuoteConfigProduct } from '../types/api/QuoteConfigProduct/QuoteConfigProduct';
import type { Tenant } from '../types/api/Tenant/Tenant';
import type { List } from '../types/list';
import { TenantDefaultMargins } from '../types/tenantDefaultMargins';
import { hasCompetitorLossDataFromFormik } from './competitors';
import { cleanedDecimal } from './decimals';
import { formatDecimalString } from './decimals';
import {
  appliedWithinNetPriceFloor,
  dropsNetPriceBelowZero,
  ExceedingDiscounts,
  grossPriceOf,
  parsedAmount,
  pendingDiscounts,
  rescaledToGrossPrice,
  settledNetPrice,
} from './discounts';
import { hasStringValue, hasValue } from './equality';
import { FormikCurrency } from './formikHelpers';
import { AppliedTierType, calculateTierAdjustment } from './tierPricing';
import { randomUUID } from './uuid';

const resolveTierPrice = (
  fixedPricingByCompany: FixedPricingByCompany | undefined,
  basePrice: Currency | undefined,
  locationId: string | null | undefined,
  productId: string | null | undefined,
): Currency | null => {
  const [tierPrice, appliedTierType] = calculateTierAdjustment(
    fixedPricingByCompany,
    basePrice,
    locationId,
    productId,
  );
  return appliedTierType !== AppliedTierType.None && tierPrice != null
    ? new Currency({
        number: formatDecimalString(tierPrice.number) || tierPrice.number,
        currency: tierPrice.currency,
      })
    : null;
};

/**
 * Default quote expiration: the project's expiration when the tenant inherits and the project
 * has one, otherwise today + the tenant's quote validity days.
 */
export const defaultQuoteExpiration = (tenant: Tenant, project?: Project | null): DateTime => {
  const projectExpiration = project?.expirationDate ?? null;
  if (tenant.inheritQuoteExpirationFromProject && hasValue(projectExpiration)) {
    return projectExpiration;
  }
  return DateTime.now().plus({ days: tenant.defaultQuoteValidityDays });
};

/**
 * Merges products by plants arrays, preserving existing products and adding new ones from source
 */
export const mergeProductsByPlants = (
  existing: ProductsByPlantFormikType[],
  source: ProductsByPlantFormikType[],
): ProductsByPlantFormikType[] => {
  const result: ProductsByPlantFormikType[] = [...existing];

  source.forEach((sourcePlant) => {
    const existingPlantIndex = result.findIndex((p) => p.plant.id === sourcePlant.plant.id);

    if (existingPlantIndex >= 0) {
      // Merge products for existing plant
      const existingProducts = result[existingPlantIndex].products;
      const newProducts = sourcePlant.products.filter(
        (sp) => !existingProducts.some((ep) => ep.product.id === sp.product.id),
      );
      result[existingPlantIndex] = {
        ...result[existingPlantIndex],
        products: [...existingProducts, ...newProducts],
      };
    } else {
      // Add new plant
      result.push(sourcePlant);
    }
  });

  return result;
};

/**
 * Converts project products to productsByPlants structure for main products
 */
export const projectProductsToProductsByPlants = (
  project: Project,
  products: ProjectProduct[],
  isMoneySwitchOn: boolean,
  marginConfiguration?: Enums.MarginConfiguration,
  defaultMargins?: TenantDefaultMargins,
  fixedPricingByCompany?: FixedPricingByCompany,
): ProductsByPlantFormikType[] => {
  // Group project products by plant
  const productsByPlantMap = new Map<string, ProjectProduct[]>();

  products.forEach((pp) => {
    const plantId = pp.product.plant.id;
    const products = productsByPlantMap.get(plantId) ?? [];
    products.push(pp);
    productsByPlantMap.set(plantId, products);
  });

  // Convert to ProductsByPlantFormikType
  // Start with project.plants, then add any plants from products that aren't in project.plants
  const projectPlantIds = new Set(project.plants.map((pp) => pp.plant.id));
  const additionalPlants = Array.from(productsByPlantMap.keys())
    .filter((plantId) => !projectPlantIds.has(plantId))
    .map((plantId) => {
      // Get any product to extract plant info for plants not in project.plants
      const products = productsByPlantMap.get(plantId)!;
      const plant = products[0].product.plant;
      return {
        plant,
        plantDistance: null,
        plantDistanceUnit: null,
        plantDriveTime: null,
      };
    });

  const plantsToProcess = [...project.plants, ...additionalPlants];

  const result = plantsToProcess
    .map((projectPlant) => {
      const productsForPlant = productsByPlantMap.get(projectPlant.plant.id) ?? [];
      return {
        clientId: randomUUID(),
        plant: {
          id: projectPlant.plant.id,
          option: {
            value: projectPlant.plant.id,
            label: projectPlant.plant.name,
          },
        },
        plantDistance: projectPlant.plantDistance ?? null,
        plantDistanceUnit: projectPlant.plantDistanceUnit ?? null,
        plantDriveTime: projectPlant.plantDriveTime?.toISO?.() ?? null,
        products: productsForPlant.map((pp) =>
          ProjectProductToQuoteProductFormik(
            pp,
            isMoneySwitchOn,
            marginConfiguration,
            defaultMargins,
            resolveTierPrice(
              fixedPricingByCompany,
              pp.price ?? undefined,
              projectPlant.plant.id,
              pp.product.id,
            ),
          ),
        ),
      };
    })
    .filter((plantEntry) => plantEntry.products.length > 0); // Only include plants that have products

  return result;
};

/**
 * Converts quote config products to productsByPlants structure for additional products
 */
export const quoteConfigProductsToProductsByPlants = (
  quoteConfig: QuoteConfig,
  isMoneySwitchOn: boolean,
  defaultMargins: TenantDefaultMargins,
  marginConfiguration?: Enums.MarginConfiguration,
): ProductsByPlantFormikType[] => {
  // Group quote config products by plant
  const productsByPlantMap = new Map<string, QuoteConfigProduct[]>();

  quoteConfig.products.forEach((qcp) => {
    const plantId = qcp.product.plant.id;
    const products = productsByPlantMap.get(plantId) ?? [];
    products.push(qcp);
    productsByPlantMap.set(plantId, products);
  });

  // Convert to ProductsByPlantFormikType
  // Start with quoteConfig.plants, then add any plants from products that aren't in quoteConfig.plants
  const quoteConfigPlantIds = new Set(quoteConfig.plants.map((p) => p.id));
  const additionalPlants = Array.from(productsByPlantMap.keys())
    .filter((plantId) => !quoteConfigPlantIds.has(plantId))
    .map((plantId) => {
      // Get any product to extract plant info for plants not in quoteConfig.plants
      const products = productsByPlantMap.get(plantId)!;
      return products[0].product.plant;
    });

  const plantsToProcess = [...quoteConfig.plants, ...additionalPlants];

  return plantsToProcess
    .map((plant) => {
      const productsForPlant = productsByPlantMap.get(plant.id) ?? [];

      return {
        clientId: randomUUID(),
        plant: {
          id: plant.id,
          option: {
            value: plant.id,
            label: plant.name,
          },
        },
        plantDistance: null, // Quote configs don't have distances
        plantDistanceUnit: null,
        plantDriveTime: null,
        products: productsForPlant.map((qcp) =>
          QuoteConfigProductToQuoteProductFormik(
            qcp,
            isMoneySwitchOn,
            defaultMargins,
            marginConfiguration,
          ),
        ),
      };
    })
    .filter((plantEntry) => plantEntry.products.length > 0); // Only include plants that have products
};

export type TierPricingDisplayOnlyContext = {
  curProductsRef: MutableRefObject<Record<string, Product>>;
  marginConfiguration: Enums.MarginConfiguration;
  defaultMargins: TenantDefaultMargins;
};

export const applyProjectPricesAsOriginal = (
  mainProductsByPlants: ProductsByPlantFormikType[],
  projectProducts: ProjectProduct[],
): ProductsByPlantFormikType[] => {
  if (projectProducts.length === 0) {
    return mainProductsByPlants;
  }
  const lines = mainProductsByPlants.flatMap((plantSection) => plantSection.products);
  const matches = matchProjectProductsToLines(projectProducts, lines);
  const projectPriceByLine = new Map(lines.map((line, index) => [line, matches[index]?.price]));
  return mainProductsByPlants.map((plantSection) => ({
    ...plantSection,
    products: plantSection.products.map((product) => {
      const projectPrice = projectPriceByLine.get(product);
      return projectPrice ? { ...product, originalPrice: FormikCurrency(projectPrice) } : product;
    }),
  }));
};

type QuoteLineFormikType = Omit<ProjectOrQuoteProductFormikType, 'displayOnly'>;

const basePriceOf = (product: QuoteLineFormikType): Currency | undefined =>
  product.originalPrice?.number
    ? new Currency({
        number: product.originalPrice.number,
        currency: product.originalPrice.currency ?? 'USD',
      })
    : undefined;

const withRecalculatedDisplayOnly = (
  product: QuoteLineFormikType,
  repriced: QuoteLineFormikType,
  displayOnlyContext: TierPricingDisplayOnlyContext | undefined,
): QuoteLineFormikType => {
  const productId = product.product.id;
  const plantProduct = hasStringValue(productId)
    ? displayOnlyContext?.curProductsRef.current[productId]
    : undefined;
  if (!hasValue(plantProduct) || !hasValue(displayOnlyContext)) {
    return repriced;
  }

  const recalculated = setRecalculatedRMCostsNoBag({
    formikProduct: repriced,
    plantProduct,
    marginConfiguration: displayOnlyContext.marginConfiguration,
    defaultMargins: displayOnlyContext.defaultMargins,
    quoteProductOverrides: { price: repriced.price },
  });

  if (!hasValue(product.withDeliveryDisplayOnly)) {
    return recalculated;
  }
  const deliveryPriced = {
    ...recalculated,
    price: { ...recalculated.price, currency: product.price.currency },
  };
  return {
    ...recalculated,
    withDeliveryDisplayOnly: setRecalculatedWithDeliveryCostsNoBag(deliveryPriced),
  };
};

const tierPricedLine = (
  product: QuoteLineFormikType,
  locationId: string | null | undefined,
  fixedPricingByCompany: FixedPricingByCompany | undefined,
  displayOnlyContext: TierPricingDisplayOnlyContext | undefined,
  onExceedingTier: ExceedingDiscounts,
  revertToBaseWhenNoTier: boolean,
): QuoteLineFormikType => {
  const productId = product.product.id;
  if (!productId) {
    return product;
  }

  const basePrice = basePriceOf(product);

  const tierPrice = resolveTierPrice(fixedPricingByCompany, basePrice, locationId, productId);

  const targetPrice =
    tierPrice ??
    (revertToBaseWhenNoTier && basePrice
      ? new Currency({
          number: formatDecimalString(basePrice.number) || basePrice.number,
          currency: basePrice.currency,
        })
      : null);

  if (!targetPrice) {
    return product;
  }

  const tierGrossPrice = parseFloat(cleanedDecimal(targetPrice.number) || '0');
  const rescaled = rescaledToGrossPrice(
    product.discounts ?? [],
    parseFloat(cleanedDecimal(product.price.number || '0')) || 0,
    tierGrossPrice,
  );
  const exceedsTier = rescaled.length > 0 && dropsNetPriceBelowZero(rescaled, tierGrossPrice);
  if (exceedsTier && onExceedingTier === ExceedingDiscounts.Keep) {
    return product;
  }
  const discounts = exceedsTier ? appliedWithinNetPriceFloor(rescaled, tierGrossPrice) : rescaled;

  const hadDiscounts = (product.discounts ?? []).length > 0;
  const price = hadDiscounts
    ? FormikCurrency(
        new Currency({
          number: settledNetPrice(discounts, tierGrossPrice).toFixed(4),
          currency: targetPrice.currency,
        }),
      )
    : FormikCurrency(targetPrice);

  if (
    !hadDiscounts &&
    formatDecimalString(product.price.number) === formatDecimalString(price.number)
  ) {
    return product;
  }

  const discounted = hadDiscounts
    ? { ...product, discounts: pendingDiscounts(discounts), price }
    : { ...product, price };
  return withRecalculatedDisplayOnly(product, discounted, displayOnlyContext);
};

const withPendingLineDiscounts = (product: QuoteLineFormikType): QuoteLineFormikType =>
  (product.discounts ?? []).length > 0
    ? { ...product, discounts: pendingDiscounts(product.discounts ?? []) }
    : product;

export const withPendingDiscounts = (
  productsByPlants: ProductsByPlantFormikType[],
): ProductsByPlantFormikType[] =>
  productsByPlants.map((plantEntry) => ({
    ...plantEntry,
    products: plantEntry.products.map(withPendingLineDiscounts),
  }));

const isAtBuyerPrice = (
  product: QuoteLineFormikType,
  locationId: string | null | undefined,
  fixedPricingByCompany: FixedPricingByCompany | undefined,
): boolean => {
  if (!hasStringValue(product.price.number)) {
    return true;
  }
  const basePrice = basePriceOf(product);
  const buyerPrice =
    resolveTierPrice(fixedPricingByCompany, basePrice, locationId, product.product.id) ?? basePrice;
  const grossPrice = grossPriceOf(product.discounts ?? [], parsedAmount(product.price.number));
  const seededPrice = hasValue(buyerPrice) ? parsedAmount(buyerPrice.number) : 0;
  return (
    grossPrice.toFixed(DISCOUNT_AMOUNT_DECIMALS) === seededPrice.toFixed(DISCOUNT_AMOUNT_DECIMALS)
  );
};

export const applyTierPricingToProductsByPlants = (
  productsByPlants: ProductsByPlantFormikType[],
  fixedPricingByCompany: FixedPricingByCompany | undefined,
  displayOnlyContext?: TierPricingDisplayOnlyContext,
  onExceedingTier: ExceedingDiscounts = ExceedingDiscounts.Keep,
  revertToBaseWhenNoTier = false,
): ProductsByPlantFormikType[] => {
  if (!fixedPricingByCompany && !revertToBaseWhenNoTier) {
    return productsByPlants;
  }

  return productsByPlants.map((plantEntry) => ({
    ...plantEntry,
    products: plantEntry.products.map((product) =>
      tierPricedLine(
        product,
        plantEntry.plant.id,
        fixedPricingByCompany,
        displayOnlyContext,
        onExceedingTier,
        revertToBaseWhenNoTier,
      ),
    ),
  }));
};

export const applyBuyerChangeToProductsByPlants = (
  productsByPlants: ProductsByPlantFormikType[],
  previousFixedPricingByCompany: FixedPricingByCompany | undefined,
  fixedPricingByCompany: FixedPricingByCompany | undefined,
  displayOnlyContext?: TierPricingDisplayOnlyContext,
): ProductsByPlantFormikType[] =>
  productsByPlants.map((plantEntry) => ({
    ...plantEntry,
    products: plantEntry.products.map((product) => {
      if (isAtBuyerPrice(product, plantEntry.plant.id, previousFixedPricingByCompany)) {
        return tierPricedLine(
          product,
          plantEntry.plant.id,
          fixedPricingByCompany,
          displayOnlyContext,
          ExceedingDiscounts.Unapply,
          true,
        );
      }
      return withPendingLineDiscounts(product);
    }),
  }));

export const dropDiscountsFromProductsByPlants = (
  productsByPlants: ProductsByPlantFormikType[],
  displayOnlyContext?: TierPricingDisplayOnlyContext,
): ProductsByPlantFormikType[] =>
  productsByPlants.map((plantEntry) => ({
    ...plantEntry,
    products: plantEntry.products.map((product) => {
      const discounts = product.discounts ?? [];
      if (discounts.length === 0) {
        return product;
      }
      const price = FormikCurrency(
        new Currency({
          number: grossPriceOf(discounts, parsedAmount(product.price.number)).toFixed(
            DISCOUNT_AMOUNT_DECIMALS,
          ),
          currency: product.price.currency ?? 'USD',
        }),
      );
      return withRecalculatedDisplayOnly(
        product,
        { ...product, discounts: [], price },
        displayOnlyContext,
      );
    }),
  }));

/**
 * Flattens productsByPlants to a simple products array (for comparison/sync)
 */
export const flattenProductsByPlants = (
  productsByPlants: ProductsByPlantFormikType[],
): Omit<ProjectOrQuoteProductFormikType, 'displayOnly'>[] => {
  return productsByPlants.flatMap((plant) => plant.products);
};

/**
 * Checks if quote config products are synced with formik additional products
 */
export const areQuoteConfigProductsSynced = (
  quoteConfig: QuoteConfig | undefined,
  formikAdditionalProductsByPlants: QuoteFormikType['additionalProductsByPlants'],
): boolean => {
  if (quoteConfig === undefined) {
    return true;
  }

  const formikProducts = flattenProductsByPlants(formikAdditionalProductsByPlants);

  return (
    quoteConfig.products.length === formikProducts.length &&
    quoteConfig.products.every((qcp) => {
      const matchingProduct = formikProducts.find((ap) => ap.product.id === qcp.product.id);
      return (
        matchingProduct !== undefined &&
        matchingProduct.price.number !== '' &&
        Number(matchingProduct.price.number.replaceAll(',', '')) ===
          Number(qcp.product.listPrice?.number?.replaceAll(',', '') ?? '0.00')
      );
    })
  );
};

export const getIsShowCompetitorInfoSectionFromQuoteFormik = (
  statusList: QuoteStatus[] | undefined,
  formikBagValues: QuoteFormikType | ApprovedQuoteFormikType,
): boolean => {
  const currentStatus = statusList?.find((s) => s.id === formikBagValues.status.id) ?? null;
  const isLost = currentStatus !== null && currentStatus.isLost();
  const isArchived = currentStatus !== null && currentStatus.isArchived();
  return isLost || (isArchived && hasCompetitorLossDataFromFormik(formikBagValues));
};

export const getCannotEditQuoteMessage = (status: QuoteStatus): string => {
  if (status.isArchived()) {
    return 'This quote is archived and cannot be edited';
  }
  if (status.isLost()) {
    return 'This quote is lost and cannot be edited';
  }
  if (status.isBuyerRejected()) {
    return 'This quote was rejected by the buyer and cannot be edited';
  }

  return 'This quote is approved and cannot be edited';
};

const firstApprovalRequestStatus = (statuses: QuoteStatus[]): QuoteStatus | undefined =>
  [...statuses]
    .sort((a, b) => a.priority - b.priority)
    .find((status) => status.isApprovalRequest());

export const findApprovalRequestStatus = async (
  statusList: List<QuoteStatus> | undefined,
  refetchStatusList: () => Promise<{ data: List<QuoteStatus> | undefined }>,
): Promise<QuoteStatus | undefined> =>
  firstApprovalRequestStatus(statusList?.items ?? []) ??
  firstApprovalRequestStatus((await refetchStatusList()).data?.items ?? []);
